import React from 'react';

import DOMPurify from 'isomorphic-dompurify';
import styled from 'styled-components';
import { prop } from 'styled-tools';

import { sanitizeAndUpdatePreviewPaths } from 'utils/textUtils';

export const RichTextBox = styled.div`
  font-family: ${prop('theme.fonts.default')};

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: ${prop('theme.fonts.fontFamilySecondary')};
  }

  h2 {
    color: ${prop('theme.colors.black')};

    &:not(.internal-h2) {
      font-size: 2.5rem;
      font-weight: 700;
      text-transform: uppercase;
    }
  }

  p {
    font-size: 0.875rem;
    font-weight: 400;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 1.125rem;
    }
  }

  a {
    color: ${prop('theme.colors.black')};
    text-decoration: none;
    padding-bottom: 0.25rem;
    border-bottom: 0.08rem solid ${(props) => props.theme.colors.black};

    &:hover {
      color: ${prop('theme.colors.black')};
      border-bottom: 0.08rem solid ${(props) => props.theme.colors.ttRed};
    }

    &.bold-link {
      color: ${prop('theme.colors.primaryRed')};
      font-weight: 700;
      text-decoration: none;

      &:hover {
        color: ${prop('theme.colors.gray')};
      }
    }
  }

  ol,
  ul {
    font-size: 0.875rem;
    font-weight: 400;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 1.125rem;
    }
  }

  li::marker {
    color: ${prop('theme.colors.primaryRed')};
  }

  && {
    .small-print {
      font-size: 0.75rem;

      @media (min-width: ${(props) => props.theme.breakpoints.xs}) {
        font-size: 0.875rem;
      }
    }

    .small-print-extra-margin {
      @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        margin-bottom: 1.75rem;
      }
    }

    .narrow-text {
      font-size: 1.25rem;
    }
  }

  .small-h5 {
    font-size: 0.9em;
  }

  .narrow-text {
    font-family: ${prop('theme.fonts.condensed')};
  }

  .brand-list {
    padding-left: 0.75rem;

    p {
      font-size: 0.875rem;
      margin-bottom: 0;
    }

    li {
      &::marker {
        color: ${prop('theme.colors.primaryRed')};
      }

      ul {
        font-size: inherit;
        list-style-type: disc;
        padding-left: 2.25rem;

        li {
          &::marker {
            color: ${prop('theme.colors.black')};
            margin-right: 0.625rem;
          }
        }
      }
    }
  }

  img {
    max-width: 100%;
  }

  sup {
    top: -0.25em;
  }

  table {
    td {
      border: 0.0625rem solid ${prop('theme.colors.black')};
      font-size: 0.875rem;
      font-weight: 400;
      padding: 0.75rem;
      vertical-align: top;

      @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        font-size: 1.125rem;
      }
    }
  }

  .inline-icon {
    display: flex;

    &::before {
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
      content: '';
      display: block;
      margin-right: 0.75rem;
      max-height: 1.25rem;
      width: 1.5rem;
    }
  }

  .video-wrapper {
    height: 0;
    padding-bottom: 56.25%;
    position: relative;

    // using !important here to ensure youtube defaults or anything inline is overriden
    iframe {
      height: 100% !important;
      left: 0;
      position: absolute;
      top: 0;
      width: 100% !important;
    }
  }
`;

export interface RichTextProps {
  richText?: string;
  component?: React.ComponentType<any>;
}

DOMPurify.setConfig({
  ADD_TAGS: ['iframe', 'embed', 'script'],
  ADD_ATTR: ['target'],
});

function RichTextField({ richText = '', component = undefined, ...props }: RichTextProps) {
  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    // if target='_blank' set rel='noopener'
    if (typeof window !== 'undefined' && node instanceof HTMLAnchorElement && node?.target === '_blank') {
      node.setAttribute('rel', 'noopener');
    }

    if (typeof window !== 'undefined' && node.localName === 'img') {
      const imgSrc = node.getAttribute('src');
      const queryParams = '?format=pjpg&auto=webp&quality=90&width=1000&disable=upscale';
      const optimizedImgSrc = imgSrc + queryParams;

      if (imgSrc && !imgSrc.includes('format')) {
        node.setAttribute('src', optimizedImgSrc);
      }

      if (node instanceof HTMLImageElement && !node.alt) {
        node.setAttribute('alt', 'uploaded image');
      }
    }
  });

  return (
    <RichTextBox
      as={component}
      dangerouslySetInnerHTML={{ __html: sanitizeAndUpdatePreviewPaths(richText) }}
      {...props}
    />
  );
}

export default RichTextField;
